import dayjs from "dayjs";
import { toast } from "react-toastify";

import { dateFormatedToDB } from "../../functions";
import { CrmPetDiagnosisModel } from "../../models/crmPetDiagnosis";
import {
  CRM_PET_DIAGNOSIS_TABS,
  createCrmPetDiagnosisFilters,
  useCrmPetDiagnosis,
} from "../../../store/crmPetDiagnosis/useCrmPetDiagnosis";

const VALID_PERIODS = ["7", "30", "90", "custom"];
const VALID_RULES = ["all", "ABSENT", "POTENTIAL_SUBSCRIBER", "NO_ADDITIONAL", "NO_PRODUCT"];
const TAB_QUERY_PREFIX = {
  [CRM_PET_DIAGNOSIS_TABS.overview]: "overview",
  [CRM_PET_DIAGNOSIS_TABS.activity]: "activity",
  [CRM_PET_DIAGNOSIS_TABS.results]: "results",
};

const TAB_FILTER_FIELDS = {
  [CRM_PET_DIAGNOSIS_TABS.overview]: ["period", "from", "to"],
  [CRM_PET_DIAGNOSIS_TABS.activity]: ["period", "from", "to"],
  [CRM_PET_DIAGNOSIS_TABS.results]: ["period", "from", "to", "rule", "minDays", "status", "contacted"],
};

export default class CrmPetDiagnosisControllerClass {
  constructor() {
    this.name = "crm-pet-diagnosis";
    this.attemptsTableName = `${this.name}-attempts-table`;
    this.attemptsPaginationName = `${this.name}-attempts-pagination`;
    this.model = CrmPetDiagnosisModel();
    this.storeId = null;
    this.initializationKey = null;
    this.loadedTabs = {};
  }

  init = async ({ storeId, search = "", history }) => {
    if (!storeId) return;

    const activeTab = this.tabFromSearch(search);
    const nextKey = `${storeId}:${this.searchKeyWithoutTab(search)}`;
    const store = useCrmPetDiagnosis.getState();

    this.storeId = storeId;

    if (this.initializationKey !== nextKey) {
      const filters = this.filtersFromSearch(search);
      store.actions.setAllFilters(filters);
      store.actions.setAttemptsPage(0);
      this.initializationKey = nextKey;
      this.loadedTabs = {};
      this.syncAllTabsUrl(history, filters);
    }

    await this.loadTabIfNeeded(activeTab);
  };

  setFilter = (tab, filter) => {
    useCrmPetDiagnosis.getState().actions.setFilters(tab, filter);
  };

  setPeriod = (tab, period) => {
    if (period === "custom") {
      this.setFilter(tab, { period });
      return;
    }

    const days = Number(period);
    this.setFilter(tab, {
      period,
      from: dayjs().subtract(days - 1, "day").format("YYYY-MM-DD"),
      to: dayjs().format("YYYY-MM-DD"),
    });
  };

  setDate = (tab, field, value) => {
    this.setFilter(tab, {
      [field]: value?.includes("/") ? dateFormatedToDB(value) : value,
      period: "custom",
    });
  };

  applyFilters = async (tab, event, history) => {
    event?.preventDefault();
    if (!this.storeId) return;

    const state = useCrmPetDiagnosis.getState().data;
    const filters = { ...state.filters[tab] };
    if (!this.isValidPeriod(filters)) return;

    const actions = useCrmPetDiagnosis.getState().actions;
    actions.setAppliedFilters(tab, filters);
    if (tab === CRM_PET_DIAGNOSIS_TABS.results) {
      actions.setAttemptsPage(0);
    }

    const nextSearch = this.syncTabUrl(history, tab, filters);
    this.initializationKey = `${this.storeId}:${this.searchKeyWithoutTab(nextSearch)}`;

    await this.loadTabData(tab, filters);
    this.loadedTabs[tab] = `${tab}:${JSON.stringify(filters)}`;
  };

  setAttemptsPage = async (page) => {
    const state = useCrmPetDiagnosis.getState().data;
    useCrmPetDiagnosis.getState().actions.setAttemptsPage(page);
    await this.model.findRecoveryAttempts(
      state.appliedFilters[CRM_PET_DIAGNOSIS_TABS.results],
      page,
      state.attemptsPagination.perPage,
    );
  };

  retrySection = async (section) => {
    const state = useCrmPetDiagnosis.getState().data;

    if (section === "usage") {
      return this.model.findUsage(
        this.storeId,
        state.appliedFilters[CRM_PET_DIAGNOSIS_TABS.activity],
      );
    }

    if (section === "diagnosisSummary") {
      return this.model.findDiagnosisSummary(
        state.appliedFilters[CRM_PET_DIAGNOSIS_TABS.overview],
      );
    }

    if (section === "resultsKpis") {
      return this.model.findRecoveryKpis(
        CRM_PET_DIAGNOSIS_TABS.results,
        state.appliedFilters[CRM_PET_DIAGNOSIS_TABS.results],
      );
    }

    if (section === "attempts") {
      return this.model.findRecoveryAttempts(
        state.appliedFilters[CRM_PET_DIAGNOSIS_TABS.results],
        state.attemptsPagination.page,
        state.attemptsPagination.perPage,
      );
    }

    return null;
  };

  async loadTabIfNeeded(tab) {
    const state = useCrmPetDiagnosis.getState().data;
    const filters = state.appliedFilters[tab];
    const loadKey = `${tab}:${JSON.stringify(filters)}`;

    if (this.loadedTabs[tab] === loadKey) return;

    await this.loadTabData(tab, filters);
    this.loadedTabs[tab] = loadKey;
  }

  async loadTabData(tab, filters) {
    const state = useCrmPetDiagnosis.getState().data;

    if (tab === CRM_PET_DIAGNOSIS_TABS.overview) {
      await this.model.findDiagnosisSummary(filters);
    }

    if (tab === CRM_PET_DIAGNOSIS_TABS.activity) {
      await this.model.findUsage(this.storeId, filters);
    }

    if (tab === CRM_PET_DIAGNOSIS_TABS.results) {
      await Promise.all([
        this.model.findRecoveryKpis(CRM_PET_DIAGNOSIS_TABS.results, filters),
        this.model.findRecoveryAttempts(filters, 0, state.attemptsPagination.perPage),
      ]);
    }

    useCrmPetDiagnosis.getState().actions.setLastUpdatedAt(new Date().toISOString());
  }

  isValidPeriod(filters) {
    if (!dayjs(filters.from).isValid() || !dayjs(filters.to).isValid()) {
      toast.error("Informe um período válido.", { theme: "colored" });
      return false;
    }
    if (dayjs(filters.from).isAfter(dayjs(filters.to))) {
      toast.error("A data inicial deve ser anterior à data final.", {
        theme: "colored",
      });
      return false;
    }
    if (dayjs(filters.to).diff(dayjs(filters.from), "day") > 89) {
      toast.error("O período máximo permitido é de 90 dias.", {
        theme: "colored",
      });
      return false;
    }
    return true;
  }

  filtersFromSearch(search) {
    const defaults = createCrmPetDiagnosisFilters();
    const params = new URLSearchParams(search);

    return Object.values(CRM_PET_DIAGNOSIS_TABS).reduce((result, tab) => {
      const prefix = TAB_QUERY_PREFIX[tab];
      const tabDefaults = defaults[tab];
      const period = params.get(this.tabParam(prefix, "period"));
      const from = params.get(this.tabParam(prefix, "from"));
      const to = params.get(this.tabParam(prefix, "to"));

      result[tab] = {
        ...tabDefaults,
        period: VALID_PERIODS.includes(period) ? period : tabDefaults.period,
        from: dayjs(from).isValid() ? from : tabDefaults.from,
        to: dayjs(to).isValid() ? to : tabDefaults.to,
      };

      if ("minDays" in tabDefaults) {
        result[tab].minDays =
          params.get(this.tabParam(prefix, "minDays")) || tabDefaults.minDays;
      }

      if ("status" in tabDefaults) {
        result[tab].status =
          params.get(this.tabParam(prefix, "status")) || tabDefaults.status;
      }

      if ("rule" in tabDefaults) {
        const rule = params.get(this.tabParam(prefix, "rule"));
        result[tab].rule = VALID_RULES.includes(rule) ? rule : tabDefaults.rule;
      }

      if ("contacted" in tabDefaults) {
        result[tab].contacted =
          params.get(this.tabParam(prefix, "contacted")) || tabDefaults.contacted;
      }

      return result;
    }, {});
  }

  syncAllTabsUrl(history, filters) {
    if (!history) return;

    const params = new URLSearchParams(history.location.search);

    Object.values(CRM_PET_DIAGNOSIS_TABS).forEach((tab) => {
      this.applyFiltersToParams(params, tab, filters[tab]);
    });

    params.delete("eventName");

    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });
  }

  syncTabUrl(history, tab, filters) {
    if (!history) return history?.location?.search || "";

    const params = new URLSearchParams(history.location.search);
    this.applyFiltersToParams(params, tab, filters);
    params.delete("eventName");
    const nextSearch = params.toString();

    history.replace({
      pathname: history.location.pathname,
      search: nextSearch,
    });

    return nextSearch;
  }

  searchKeyWithoutTab(search) {
    const params = new URLSearchParams(search);
    params.delete("tab");
    return params.toString();
  }

  tabFromSearch(search) {
    const params = new URLSearchParams(search);
    const tab = params.get("tab");
    return Object.values(CRM_PET_DIAGNOSIS_TABS).includes(tab)
      ? tab
      : CRM_PET_DIAGNOSIS_TABS.overview;
  }

  tabParam(prefix, field) {
    return `${prefix}${field.charAt(0).toUpperCase()}${field.slice(1)}`;
  }

  applyFiltersToParams(params, tab, filters) {
    const prefix = TAB_QUERY_PREFIX[tab];

    TAB_FILTER_FIELDS[tab].forEach((field) => {
      const value = filters[field];
      if (field === "minDays" && filters.rule !== "ABSENT") {
        params.delete(this.tabParam(prefix, field));
        return;
      }
      if (value) params.set(this.tabParam(prefix, field), value);
      else params.delete(this.tabParam(prefix, field));
    });
  }
}
